import React, { useState, useEffect, useRef } from 'react';
import { Timer, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const SleepTimer = ({ onTimerEnd }) => {
  const { t } = useLanguage();
  const [showMenu, setShowMenu] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const intervalRef = useRef(null);

  const options = [5, 10, 15, 20, 30];

  useEffect(() => {
    if (remaining > 0) {
      intervalRef.current = setInterval(() => {
        setRemaining(prev => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            onTimerEnd?.();
            toast.info(t('timer.title') + ' - ' + t('timer.off'));
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [remaining > 0]);

  const startTimer = (minutes) => {
    setRemaining(minutes * 60);
    setShowMenu(false);
    toast.success(`${t('timer.active')} - ${minutes} ${t('timer.minutes')}`);
  };

  const stopTimer = () => {
    clearInterval(intervalRef.current);
    setRemaining(0);
    setShowMenu(false);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="relative" data-testid="sleep-timer">
      <Button
        data-testid="sleep-timer-btn"
        variant="ghost"
        size="sm"
        onClick={() => setShowMenu(!showMenu)}
        className={`rounded-full px-3 gap-1.5 ${remaining > 0 ? 'text-gold bg-gold/10' : ''}`}
      >
        <Timer className="w-4 h-4" />
        {remaining > 0 && <span className="text-xs font-mono">{formatTime(remaining)}</span>}
      </Button>

      {showMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
          <div className="absolute right-0 bottom-12 z-50 bg-card border border-border rounded-xl shadow-xl p-3 min-w-[180px]" data-testid="sleep-timer-menu">
            <p className="text-xs font-bold text-foreground mb-2 px-1">{t('timer.title')}</p>
            <div className="grid grid-cols-3 gap-1.5">
              {options.map(m => (
                <button
                  key={m}
                  data-testid={`timer-${m}`}
                  onClick={() => startTimer(m)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${remaining > 0 && Math.ceil(remaining / 60) === m ? 'bg-gold/20 text-gold' : 'hover:bg-muted text-foreground'}`}
                >
                  {m} {t('timer.minutes')}
                </button>
              ))}
              <button
                data-testid="timer-off"
                onClick={stopTimer}
                className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
              >
                {t('timer.off')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SleepTimer;
